/**
 * Importing a client's statement: the upload, the preview it produces, and the
 * apply that books it.
 *
 * The whole flow is three server calls and a little local state — which scheme
 * the advisor resolved to what, and whether the file is still waiting on a
 * password. That state is held here rather than in the screen so the screen
 * renders a finished view and nothing about the API shape reaches it.
 */
import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useMutation, useLazyQuery } from '@apollo/client';
import { UPLOAD_PORTFOLIO_STATEMENT, APPLY_PORTFOLIO_STATEMENT } from '../mutations/statement';
import {
  GET_STATEMENT_PREVIEW, GET_SUPPORTED_GOALS, LIST_STATEMENT_UPLOADS,
} from '../queries/statement';
import { asClient } from '../clientScope';
import { useBackgroundJobs } from '../backgroundJobs';
import {
  arnBucket, BUCKET, BUCKET_ORDER, investorBlocker, isBlocking, matchOf, needsChoice,
  needsContact, takesStartDate, totalNum, willBook, willCreateClient,
} from '../../lib/statement';

/** The message the backend returns when a statement will not open. */
const LOCKED = /password-protected/i;

/**
 * The reader's decisions about one import — everything the preview is re-asked
 * with and the apply books with. One object, so the two calls cannot be sent
 * different answers. `startDate` stays null until the reader picks one: until
 * then the backend's own choice stands, and nothing is sent.
 */
const NO_INPUTS = { overrides: {}, goalType: null, startDate: null };

/**
 * The variables for BOTH the preview query and the apply mutation, from the
 * reader's decisions. Built in one place so a new decision cannot reach one
 * call and not the other.
 *
 * `overrides` goes as the complete set — the resolver drops anything not sent.
 * `startDate` goes only for a holdings sheet: the backend refuses it for every
 * other format, so it is left off entirely rather than sent as null.
 */
export function buildStatementInputs(inputs, upload) {
  return {
    uploadId: upload?.id,
    goalType: inputs.goalType ?? null,
    overrides: Object.entries(inputs.overrides).map(([schemeKey, o]) => ({
      schemeKey,
      securitySymbol: o.skip ? null : o.securitySymbol,
      skip: Boolean(o.skip),
    })),
    ...(takesStartDate(upload) && inputs.startDate ? { startDate: inputs.startDate } : {}),
  };
}

/**
 * Everything the screen shows about one import, derived once.
 *
 * The preview arrives as four flat lists that all key on `schemeKey`; the
 * screen wants them joined — a holding needs the match that decided whether it
 * will be booked, and the book split needs the advisor field that only travels
 * on the rows. Joined here so no screen does it twice.
 */
function derive(preview, ownArn, overrides, contact) {
  if (!preview) return null;

  const schemes = preview.schemes ?? [];
  const byKey = new Map(schemes.map((s) => [s.schemeKey, s]));

  /* What each scheme is WORTH, carried over from the position that shares its
     key. The match rows do not price themselves, and without the money beside
     them a suspect match on ₹4 lakh reads exactly like one on nothing —
     which is the difference between a mistake worth stopping for and one worth
     a shrug. Summed rather than taken, so a scheme spread over more than one
     row still totals correctly. */
  const valueOf = new Map();
  (preview.holdings ?? []).forEach((h) => {
    const prior = valueOf.get(h.schemeKey) ?? { units: 0, marketValue: 0, costValue: 0 };
    valueOf.set(h.schemeKey, {
      units: prior.units + (h.units ?? 0),
      marketValue: prior.marketValue + (h.marketValue ?? 0),
      costValue: prior.costValue + (h.costValue ?? 0),
    });
  });

  /* The statuses here are already the SERVER's answer to the overrides — the
     preview is re-fetched after every choice, so nothing is simulated. The
     local map is consulted only for what the server cannot say: which of the
     unmatched rows are unmatched because the reader skipped them, rather than
     because nothing was found. */
  const resolved = schemes.map((raw) => {
    const value = valueOf.get(raw.schemeKey) ?? { units: null, marketValue: null, costValue: null };
    const override = overrides[raw.schemeKey];
    return {
      ...raw,
      ...value,
      overridden: raw.matchedVia === 'OVERRIDE',
      skipped: Boolean(override?.skip),
    };
  });
  const resolvedByKey = new Map(resolved.map((s) => [s.schemeKey, s]));

  const holdings = (preview.holdings ?? []).map((h) => {
    const scheme = resolvedByKey.get(h.schemeKey) ?? byKey.get(h.schemeKey);
    return {
      ...h,
      match: scheme?.status ?? h.matchStatus,
      masterSchemeName: scheme?.masterSchemeName ?? null,
      securitySymbol: scheme?.securitySymbol ?? h.securitySymbol,
      warnings: scheme?.warnings ?? [],
      bucket: arnBucket(h.advisor, ownArn),
      willBook: willBook(scheme?.status ?? h.matchStatus),
    };
  });

  const transactions = (preview.transactions ?? []).map((t) => ({
    ...t,
    schemeName: byKey.get(t.schemeKey)?.schemeName ?? t.schemeKey,
    amc: byKey.get(t.schemeKey)?.amc ?? null,
  }));

  /* Live value by whose book it sits on. Closed positions are left out: a
     folio exited two years ago is history, not somebody's book. */
  const live = holdings.filter((h) => (h.marketValue ?? 0) > 0);
  const total = live.reduce((t, h) => t + (h.marketValue ?? 0), 0);
  const arnSplit = BUCKET_ORDER.map((key) => {
    const rows = live.filter((h) => h.bucket === key);
    const value = rows.reduce((t, h) => t + (h.marketValue ?? 0), 0);
    const arns = [...new Set(rows.map((h) => h.advisor).filter((a) => a && !/^direct$/i.test(a)))];
    return {
      key,
      label: BUCKET[key].label,
      color: BUCKET[key].color,
      tone: BUCKET[key].tone,
      value,
      schemes: rows.length,
      share: total ? (value / total) * 100 : 0,
      detail: key === 'own' ? (ownArn || null) : (arns.length === 1 ? arns[0] : arns.length ? `${arns.length} distributors` : null),
    };
  });

  const unresolved = resolved.filter((s) => needsChoice(s.status));
  const blocking = (preview.issues ?? []).filter(isBlocking);

  return {
    preview,
    upload: preview.upload,
    /* Who the statement resolved to, what booking it would do to them, and
       the reason it would be refused if it would. */
    investor: preview.investor ?? null,
    creatingClient: willCreateClient(preview.investor),
    needsContact: needsContact(preview.investor),
    investorBlock: investorBlocker(preview.investor, {
      hasContact: Boolean(contact.email?.trim() || contact.phone?.trim()),
    }),
    totals: preview.totals ?? {},
    issues: preview.issues ?? [],
    blocking,
    /* In the order the backend returned, which is stable across rechecks: a
       row that has just been resolved keeps its place. */
    schemes: resolved,
    holdings,
    transactions,
    arnSplit,
    /* What apply would do as things stand. Counted off the resolved schemes,
       so choosing a candidate moves the count before anything is sent. */
    bookable: resolved.filter((s) => willBook(s.status)).length,
    skipping: resolved.filter((s) => !willBook(s.status)).length,
    /* The money that would NOT be booked as things stand — the figure that says
       whether the schemes left out are worth going back for. */
    skippedValue: resolved
      .filter((s) => !willBook(s.status))
      .reduce((t, s) => t + (s.marketValue ?? 0), 0),
    unresolved,
    /* Every gate on the button in one place: the schemes must be settled,
       something must be left to book, and the investor must be someone the
       backend will accept — four of its statuses refuse the write outright. */
    canApply: unresolved.length === 0
      && resolved.some((s) => willBook(s.status))
      && !investorBlocker(preview.investor, {
        hasContact: Boolean(contact.email?.trim() || contact.phone?.trim()),
      }),
    value: totalNum(preview.totals, 'market_value'),
    cost: totalNum(preview.totals, 'cost_value'),
    /* Counted off the schemes rather than read from `totals`: the backend
       leaves `totals.matched` and `totals.unmatched` at what the original
       parse found, so they go stale the moment anything is overridden. */
    matchedCount: resolved.filter((s) => willBook(s.status)).length,
    unmatchedCount: resolved.filter((s) => !willBook(s.status)).length,
  };
}

/**
 * One statement import, start to finish.
 *
 * `clientId` scopes every call to the client the portfolio is being built for
 * (X-Target-User-Id). Without it the statement books onto the signed-in
 * advisor's own account, which is almost never what anybody wants.
 */
export function useStatementImport(clientId, ownArn) {
  const scope = useMemo(() => asClient(clientId), [clientId]);

  const [preview, setPreview] = useState(null);
  /* Which scheme resolved to what, the goal, and the start date — see
     NO_INPUTS. The goal is sent to the preview too, because it decides what the
     portfolio is called. */
  const [inputs, setInputs] = useState(NO_INPUTS);
  const { overrides, goalType } = inputs;
  const [error, setError] = useState(null);
  /* Why the last start date was refused, shown under the picker rather than in
     the page-wide note: it is about that one field. */
  const [startDateError, setStartDateError] = useState(null);
  /* The file still waiting on a password, kept so the retry re-sends the same
     bytes rather than asking for the file again. `tried` is what separates
     "this needs a password" from "that password was wrong" — the backend
     returns the same message for both. */
  const [locked, setLocked] = useState(null);
  const [applied, setApplied] = useState(null);
  /* The investor's contact details, as the account would be created against.
     Seeded from the statement and corrigible: a new client is created from
     these, so a typo in the document must not become an account. */
  const [contact, setContactState] = useState({ email: '', phone: '' });
  /* A re-ask in flight, so the schemes table can say it is waiting rather than
     showing an answer that is about to change. */
  const [rechecking, setRechecking] = useState(false);

  const [runUpload, uploadState] = useMutation(UPLOAD_PORTFOLIO_STATEMENT);
  const [runApply, applyState] = useMutation(APPLY_PORTFOLIO_STATEMENT);
  const { start: followJob } = useBackgroundJobs();
  const [refetchPreview] = useLazyQuery(GET_STATEMENT_PREVIEW, { fetchPolicy: 'network-only' });

  /**
   * Ask the server what the current decisions mean.
   *
   * Overrides are a COMPLETE set on the resolver — sending one drops the rest —
   * so every call carries all of them. On failure the decision that caused it
   * is rolled back, because a rejected override left in place would leave the
   * table disagreeing with the preview beside it.
   *
   * `report` is where a refusal is shown — the page-wide note unless the
   * caller has a field of its own to put it under.
   */
  const recheck = useCallback(async (next, prior, report = setError) => {
    if (!preview?.upload?.id) return;
    setInputs(next);
    setRechecking(true);
    setError(null);
    try {
      const { data } = await refetchPreview({
        variables: buildStatementInputs(next, preview.upload),
        context: scope,
      });
      const result = data?.getPortfolioStatementPreview;
      if (result?.success && result.preview) {
        setPreview(result.preview);
        return;
      }
      /* The previous preview is kept: it is still the truth about everything
         the rejected choice did not touch. */
      report(result?.message || 'That choice could not be applied.');
      setInputs(prior);
    } catch (e) {
      report(e.message);
      setInputs(prior);
    } finally {
      setRechecking(false);
    }
  }, [preview, refetchPreview, scope]);

  /** Correct the email or mobile the client would be created against. */
  const setContact = useCallback((patch) => {
    setContactState((prior) => ({ ...prior, ...patch }));
  }, []);

  const reset = useCallback(() => {
    setPreview(null);
    setInputs(NO_INPUTS);
    setError(null);
    setStartDateError(null);
    setLocked(null);
    setApplied(null);
    setContactState({ email: '', phone: '' });
  }, []);

  /* Named for the action, not the noun: `upload` is the statement RECORD this
     hook returns, and a function of the same name would quietly shadow it. */
  const uploadFile = useCallback(async (file, password) => {
    if (!file) return;
    setError(null);
    setApplied(null);
    try {
      const { data } = await runUpload({
        variables: { file, password: password || null },
        context: scope,
      });
      const result = data?.uploadPortfolioStatement;
      if (result?.success && result.preview) {
        setPreview(result.preview);
        /* A new statement starts from the backend's own answers. The goal is
           kept, as it always was: it describes the client, not the file. */
        setInputs((prior) => ({ ...NO_INPUTS, goalType: prior.goalType }));
        setStartDateError(null);
        setLocked(null);
        setContactState({
          email: result.preview.investor?.email ?? '',
          phone: result.preview.investor?.phone ?? '',
        });
        return;
      }
      /* A statement that will not open is not a failed upload — it is one
         waiting on the investor's PAN, and the screen asks for it. */
      if (LOCKED.test(result?.message ?? '')) {
        setLocked({ file, name: file.name, tried: Boolean(password) });
        return;
      }
      setError(result?.message || 'The statement could not be read.');
    } catch (e) {
      setError(e.message);
    }
  }, [runUpload, scope]);

  /** The PAN, against the statement still waiting on one. */
  const unlock = useCallback((password) => {
    if (locked?.file) return uploadFile(locked.file, password);
    return undefined;
  }, [locked, uploadFile]);

  /**
   * Point a scheme at a fund, or leave it out — for ANY scheme, not only the
   * ambiguous ones. Re-pointing a scheme the matcher got wrong is the case
   * that matters: an ISIN can resolve confidently to a different fund, and
   * nothing else on the screen can correct it.
   */
  const resolveScheme = useCallback((schemeKey, choice) => {
    const next = { ...inputs.overrides };
    if (!choice) delete next[schemeKey];
    else next[schemeKey] = choice;
    return recheck({ ...inputs, overrides: next }, inputs);
  }, [inputs, recheck]);

  /** The goal the portfolio is created under; re-asks so the name follows. */
  const setGoalType = useCallback(
    (next) => recheck({ ...inputs, goalType: next }, inputs),
    [inputs, recheck],
  );

  /**
   * The day a holdings sheet's funds are booked as bought. Re-asks, because it
   * moves every transaction date, cost value and the checks with it; a refusal
   * is shown under the picker and the date goes back to what it was.
   */
  const setStartDate = useCallback((next) => {
    if (!takesStartDate(preview?.upload)) return undefined;
    setStartDateError(null);
    return recheck({ ...inputs, startDate: next }, inputs, setStartDateError);
  }, [preview, inputs, recheck]);

  const apply = useCallback(async () => {
    if (!preview?.upload?.id) return;
    /* The date the preview on screen was built with — the reader's, else the
       one the backend chose and echoed — so the booking is what was shown. */
    const variables = buildStatementInputs(
      { ...inputs, startDate: inputs.startDate ?? preview.startDate ?? null },
      preview.upload,
    );
    setError(null);
    try {
      const { data } = await runApply({
        variables: {
          ...variables,
          /* What an account would be created against — the statement's own
             details unless the reader corrected them. */
          investorEmail: contact.email?.trim() || null,
          investorPhone: contact.phone?.trim() || null,
        },
        context: scope,
      });
      const result = data?.applyPortfolioStatement;
      if (!result?.success) {
        setError(result?.message || 'The statement could not be booked.');
        return;
      }
      setApplied(result);
      /* The apply has already queued the portfolio's analysis (and, after it,
         the review). Handed to the tracker so it follows that work from here —
         the toast arrives wherever the reader has gone by then. */
      followJob(Promise.resolve(result), {
        subject: result.securitySymbol,
        clientUserId: result.clientUserId ?? clientId ?? null,
        name: result.portfolioName,
      });
      /* Re-read so the preview reflects what is now booked — holdings read
         UNCHANGED and the rows that were written read ALREADY_BOOKED. */
      const { data: fresh } = await refetchPreview({ variables, context: scope });
      const refreshed = fresh?.getPortfolioStatementPreview?.preview;
      if (refreshed) setPreview(refreshed);
    } catch (e) {
      setError(e.message);
    }
  }, [preview, inputs, contact, runApply, refetchPreview, scope, followJob, clientId]);


  const derived = useMemo(
    () => derive(preview, ownArn, overrides, contact),
    [preview, ownArn, overrides, contact],
  );

  return {
    ...(derived ?? {
      preview: null, upload: null, investor: null, creatingClient: false,
      needsContact: false, investorBlock: null, totals: {}, issues: [], blocking: [],
      matchedCount: 0, unmatchedCount: 0,
      schemes: [], holdings: [], transactions: [], arnSplit: [],
      bookable: 0, skipping: 0, skippedValue: 0, unresolved: [], canApply: false,
      value: null, cost: null,
    }),
    overrides,
    contact,
    setContact,
    goalType,
    setGoalType,
    /* The date in effect: the reader's, else the one the backend chose. */
    startDate: inputs.startDate ?? preview?.startDate ?? null,
    canChooseStartDate: takesStartDate(preview?.upload),
    setStartDate,
    startDateError,
    rechecking,
    locked,
    error,
    applied,
    uploading: uploadState.loading,
    applying: applyState.loading,
    uploadFile,
    unlock,
    resolveScheme,
    apply,
    reset,
  };
}

/** Statements uploaded for this client, newest first. */
export function useStatementUploads(clientId, limit = 10) {
  const scope = useMemo(() => asClient(clientId), [clientId]);
  const [load, { data, loading, error }] = useLazyQuery(LIST_STATEMENT_UPLOADS, {
    fetchPolicy: 'network-only',
  });
  const fetch = useCallback(
    () => load({ variables: { limit }, context: scope }),
    [load, limit, scope],
  );
  return {
    uploads: data?.listPortfolioStatementUploads?.uploads ?? [],
    loading,
    error,
    fetch,
  };
}

/**
 * The goals a portfolio can be created under — nine active kinds, with the
 * name the API takes and the words a person reads.
 *
 * Cached rather than re-fetched: the list is the same for every import, and a
 * picker that flickers on each upload reads as though the options changed.
 */
export function useSupportedGoals() {
  const { data, loading, error } = useQuery(GET_SUPPORTED_GOALS, {
    variables: { limit: 30, language: 'en' },
    fetchPolicy: 'cache-first',
  });
  const goals = useMemo(
    () => (data?.getSupportedGoals ?? []).filter((g) => g.isActive),
    [data],
  );
  return {
    goals,
    /* For a SearchSelect: the API's own `name` is the value, because that is
       what `goalType` takes. */
    options: goals.map((g) => ({ value: g.name, label: g.displayName || g.name })),
    loading,
    error,
  };
}

/** Schemes the advisor still has to decide, worst first — for the badge count. */
export const unresolvedCount = (schemes) =>
  (schemes ?? []).filter((s) => matchOf(s.status).rank <= 1).length;
